import React, { useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Agent } from '@dreamai/types';
import { ReactComponent as MyDocumentsIcon } from '../../icons/my-documents.svg';
import { useAgent } from '../../contexts/AgentContext';
import './dreamie-tabs.scss';
import NotificationCount from './NotificationCount';
import HasUnreadNotificationCount from './HasUnreadNotificationCount';
import ManagerNameListener from './sidebar/ManagerNameListener';

interface AgentTabsProps {
  activeAgentId?: string | null;
  onTabClick?: (agentId: string) => void;
}

const AgentTabs: React.FC<AgentTabsProps> = ({ activeAgentId, onTabClick }) => {
  const location = useLocation();
  const isDocsRoute = location.pathname.startsWith('/docs');
  const { agents, loading, error, loadAgents } = useAgent();

  // Load agents when component mounts
  useEffect(() => {
    loadAgents();
  }, [loadAgents]);

  // Filter out Blank Manager (used only for fast-chat) and sort to put Business Manager first
  const sortedAgents = [...agents]
    .filter((agent) => agent.agent_type !== 'Blank Manager')
    .sort((a, b) => {
      if (a.agent_type === 'Business Manager') return -1;
      if (b.agent_type === 'Business Manager') return 1;
      return 0;
    });

  // Loading state
  if (loading) {
    return (
      <div className='dreamie-tabs'>
        <div className='dreamie-tab loading'>
          <div className='dreamie-image-mask'></div>
          <span className='dreamie-name'>Loading...</span>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className='dreamie-tabs'>
        <div className='dreamie-tab error'>
          <span className='dreamie-name'>Failed to load agents</span>
        </div>
      </div>
    );
  }

  return (
    <>
      <ManagerNameListener />
      <div className='dreamie-tabs'>
        {sortedAgents.map((agent) => (
          <div
            key={agent.id}
            className={`dreamie-tab ${agent.id === activeAgentId ? 'active' : ''}`}
            onClick={() => onTabClick?.(agent.id)}
          >
            <div className='dreamie-image-mask'>
              {agent.image_url && (
                <img
                  src={agent.image_url}
                  alt={agent.name || agent.agent_type}
                />
              )}
            </div>
            <span className='dreamie-name'>
              {agent.nickname || agent.name || agent.agent_type}'s space
            </span>
            <HasUnreadNotificationCount agentId={agent.id} />
            <NotificationCount agentId={agent.id} />
          </div>
        ))}
      </div>
    </>
  );
};

export default AgentTabs;
